/**
 * Variant rules for special units in Memoir '44.
 *
 * A unit with `variant` set inherits all base-type rules and then overrides
 * only the specific fields listed here.  Add new variants here; no other
 * game-logic files need to change.
 */

export interface VariantEffect {
  name: string;
  description: string;

  // ── Movement / battle overrides ────────────────────────────────────────
  /** Infantry: max hexesMoved that still allows battling (default 1). */
  canBattleAfterMoveHexes?: number;
  /** Override the unit type's normal move distance. */
  maxMoveDistance?: number;
  /**
   * Infantry: if set, a unit that has moved up to this many hexes may still
   * battle even when entering terrain that normally blocks battling after a
   * move (forest, town, hedgerow). Unlike `canBattleAfterMoveHexes`, this does
   * NOT extend the normal move-then-battle range — it only lifts the
   * terrain-based `blocksBattleAfterMove` restriction.
   */
  ignoreBlocksBattleAfterMoveMaxHexes?: number;

  // ── Retreat overrides ───────────────────────────────────────────────────
  /**
   * If set, each Flag rolled lets the unit retreat between [min, max] hexes
   * (inclusive), rather than exactly 1 hex per flag. e.g. [1, 3] with 2 flags
   * allows a total retreat of 2–6 hexes.
   */
  retreatHexRangePerFlag?: [number, number];

  // ── Medals ──────────────────────────────────────────────────────────────
  /** Victory medals awarded to the opposing side when this unit is eliminated (default 1). */
  medalsOnElimination?: number;

  // ── Battle range ────────────────────────────────────────────────────────
  /**
   * Override the unit type's normal maximum attack range.
   * e.g. 2 for ski troops (close-combat specialists, shorter range than standard infantry).
   */
  maxBattleRange?: number;

  // ── Visual ──────────────────────────────────────────────────────────────
  /** Short label used by UI code to look up the badge drawing routine. */
  badge: string;
}

export const UNIT_VARIANTS: Record<string, VariantEffect> = {

  commando: {
    name: 'British Commando',
    description: 'Elite infantry. May move 1 or 2 hexes and still battle.',
    canBattleAfterMoveHexes: 2,
    badge: 'commando',
  },

  airborne: {
    name: 'British Airborne',
    description: 'Elite infantry. May move 1 or 2 hexes and still battle.',
    canBattleAfterMoveHexes: 2,
    badge: 'airborne',
  },

  ranger: {
    name: 'US Ranger',
    description: 'Elite infantry. May move 1 or 2 hexes and still battle. Can scale cliffs.',
    canBattleAfterMoveHexes: 2,
    badge: 'ranger',
  },

  resistance: {
    name: 'French Resistance',
    description: 'Only 3 figures. A unit that moves 1 hex may always battle, even after entering '
      + 'terrain that would normally forbid it (forest, town, hedgerow) — but moving 2 hexes into '
      + 'such terrain still forbids battling. When retreating, may retreat 1-3 hexes per Flag rolled.',
    ignoreBlocksBattleAfterMoveMaxHexes: 1,
    retreatHexRangePerFlag: [1, 3],
    badge: 'resistance',
  },

  'ski-troops': {
    name: 'Finnish Ski Troops',
    description: 'May move up to 3 hexes and still battle. Battle range 2 hexes (close-combat specialists). '
      + 'May battle after entering terrain that normally forbids it (forest, town, hedgerow) regardless of distance moved. '
      + 'When retreating, may retreat 1–3 hexes per Flag rolled.',
    maxMoveDistance: 3,
    canBattleAfterMoveHexes: 3,
    ignoreBlocksBattleAfterMoveMaxHexes: 3,
    maxBattleRange: 2,
    retreatHexRangePerFlag: [1, 3],
    badge: 'ski-troops',
  },

  'special-forces': {
    name: 'Axis Special Forces',
    description: '4-figure armor unit. Eliminating this unit counts as two Victory Medals for the Allied player.',
    medalsOnElimination: 2,
    badge: 'panzer-sf',
  },

  'panzer-sf': {
    name: 'Axis Special Forces Armor',
    description: '4-figure armor unit.',
    badge: 'panzer-sf',
  },

  'panzer-grenadier': {
    name: 'Panzer Grenadier',
    description: 'Elite motorised infantry. May move 1 or 2 hexes and still battle.',
    canBattleAfterMoveHexes: 2,
    badge: 'panzer-sf',
  },

};

/** Returns the variant effect for a unit, or undefined for standard units. */
export function getVariantEffect(variant?: string): VariantEffect | undefined {
  if (!variant) return undefined;
  return UNIT_VARIANTS[variant];
}
